/**
 * Tutorial Component
 * Interactive onboarding guide for first-time users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dismiss24Regular } from '@fluentui/react-icons';
import './Tutorial.css';

export interface TutorialStep {
  id: string;
  title: string;
  content: string;
  contentHTML?: string; // Optional HTML content instead of plain text
  target?: string; // CSS selector for element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'bottom-screen';
  action?: 'click' | 'type' | 'none';
  actionTarget?: string;
  actionDescription?: string;
  onEnter?: () => void; // Callback when step becomes active
  onExit?: () => void; // Callback when leaving step (clicking Next/Previous)
}

interface TutorialProps {
  steps: TutorialStep[];
  onComplete: () => void;
  onSkip: () => void;
}

export function Tutorial({ steps, onComplete, onSkip }: TutorialProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightElement, setHighlightElement] = useState<HTMLElement | null>(null);

  const step = steps[currentStep];

  // Highlight target element and trigger onEnter callback
  useEffect(() => {
    // Call onEnter callback when step becomes active
    if (step.onEnter) {
      step.onEnter();
    }

    // Function to find and highlight element
    const findAndHighlightElement = () => {
      if (step.target) {
        const element = document.querySelector(step.target) as HTMLElement;
        
        // Debug: check what's actually in the DOM
        const container = document.querySelector('.validation-mode-container');
        const contentDiv = container?.querySelector('.content');
        const articles = container?.querySelectorAll('.article');
        const contentArticles = contentDiv?.querySelectorAll('.article');
        const firstArticle = articles?.[0];
        const h2 = firstArticle?.querySelector('h2');
        
        console.log('Tutorial: Looking for element', { 
          stepId: step.id, 
          target: step.target, 
          found: !!element,
          element: element,
          debug: {
            containerExists: !!container,
            contentDivExists: !!contentDiv,
            containerHTML: container?.innerHTML?.substring(0, 200),
            articlesCount: articles?.length,
            contentArticlesCount: contentArticles?.length,
            firstArticleExists: !!firstArticle,
            h2Exists: !!h2,
            h2Text: h2?.textContent?.substring(0, 50)
          }
        });
        
        if (element) {
          setHighlightElement(element);
          // Don't scroll for bottom-screen positioned tooltips
          if (step.position !== 'bottom-screen') {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return true;
        }
        return false;
      } else {
        setHighlightElement(null);
        return true;
      }
    };

    // Try immediately
    if (!findAndHighlightElement()) {
      // If not found, retry with increasing delays (longer for steps that need DOM to populate)
      const delays = [200, 500, 800, 1200, 1500, 2000];
      const timers = delays.map(delay => 
        setTimeout(() => {
          findAndHighlightElement();
        }, delay)
      );

      return () => timers.forEach(timer => clearTimeout(timer));
    }
  }, [step]);

  const handleNext = useCallback(() => {
    // Call onExit callback before leaving step
    if (step.onExit) {
      step.onExit();
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  }, [currentStep, steps.length, onComplete, step]);

  const handlePrevious = useCallback(() => {
    // Call onExit callback before leaving step
    if (step.onExit) {
      step.onExit();
    }
    
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, step]);

  const handleSkip = useCallback(() => {
    onSkip();
  }, [onSkip]);

  const getTooltipPosition = () => {
    if (!highlightElement || !step.position || step.position === 'center') {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    if (step.position === 'bottom-screen') {
      return { 
        top: 'auto',
        left: '50%'
        // Transform and bottom handled by CSS class with !important
      };
    }

    const rect = highlightElement.getBoundingClientRect();
    const offset = 20;

    switch (step.position) {
      case 'top':
        return {
          top: `${rect.top - offset}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translate(-50%, -100%)',
        };
      case 'bottom':
        return {
          top: `${rect.bottom + offset}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translate(-50%, 0)',
        };
      case 'left':
        return {
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.left - offset}px`,
          transform: 'translate(-100%, -50%)',
        };
      case 'right':
        return {
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.right + offset}px`,
          transform: 'translate(0, -50%)',
        };
      default:
        return {};
    }
  };

  return (
    <>
      {/* Overlay - click disabled, only Skip button closes tutorial */}
      <div className="tutorial-overlay" />

      {/* Highlight spotlight */}
      {highlightElement && (
        <div
          className="tutorial-spotlight"
          style={{
            top: highlightElement.getBoundingClientRect().top - 8,
            left: highlightElement.getBoundingClientRect().left - 8,
            width: highlightElement.getBoundingClientRect().width + 16,
            height: highlightElement.getBoundingClientRect().height + 16,
          }}
        />
      )}

      {/* Tutorial tooltip */}
      <div
        className={`tutorial-tooltip ${step.position === 'bottom-screen' ? 'tutorial-tooltip-bottom' : ''}`}
        style={getTooltipPosition()}
      >
        <div className="tutorial-header">
          <h3>{step.title}</h3>
          <button
            className="tutorial-close"
            onClick={handleSkip}
            aria-label="Skip tutorial"
            title="Skip tutorial"
          >
            <Dismiss24Regular />
          </button>
        </div>

        <div className="tutorial-content">
          {step.contentHTML ? (
            <div dangerouslySetInnerHTML={{ __html: step.contentHTML }} />
          ) : (
            <p>{step.content}</p>
          )}
          
          {step.actionDescription && (
            <div className="tutorial-action">
              <strong>Try it:</strong> {step.actionDescription}
            </div>
          )}
        </div>

        <div className="tutorial-footer">
          <div className="tutorial-progress">
            {steps.map((_, index) => (
              <span
                key={index}
                className={`tutorial-dot ${index === currentStep ? 'active' : ''} ${
                  index < currentStep ? 'completed' : ''
                }`}
              />
            ))}
          </div>

          <div className="tutorial-buttons">
            <button
              className="tutorial-btn tutorial-btn-secondary"
              onClick={handleSkip}
            >
              Skip Tour
            </button>
            {currentStep > 0 && (
              <button
                className="tutorial-btn tutorial-btn-secondary"
                onClick={handlePrevious}
              >
                Previous
              </button>
            )}
            <button
              className="tutorial-btn tutorial-btn-primary"
              onClick={handleNext}
            >
              {currentStep < steps.length - 1 ? 'Next' : 'Finish'}
            </button>
          </div>
        </div>

        <div className="tutorial-step-counter">
          Step {currentStep + 1} of {steps.length}
        </div>
      </div>
    </>
  );
}

export default Tutorial;
